<template>
  <div class="modal-overlay" @click="$emit('close')" data-cy="route-map-modal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>Route Map Visualization</h2>
        <button @click="$emit('close')" class="btn-close">×</button>
      </div>
      
      <div class="modal-body">
        <div class="map-controls">
          <div class="vehicle-controls">
            <h4>Vehicle Layers</h4>
            <div class="vehicle-checkboxes">
              <label 
                v-for="(vehicle, index) in vehicleRoutes" 
                :key="index"
                class="vehicle-checkbox"
              >
                <input 
                  type="checkbox" 
                  :checked="vehicle.visible"
                  @change="toggleVehicleVisibility(index)"
                />
                <span 
                  class="checkbox-color" 
                  :style="{ backgroundColor: vehicle.color }"
                ></span>
                <span class="vehicle-name">{{ vehicle.name }}</span>
                <span class="vehicle-stats">
                  {{ (vehicle.distance / 1000).toFixed(1) }}km
                </span>
              </label>
            </div>
          </div>
        </div>
        
        <div class="map-container">
          <div ref="mapContainer" class="map-display"></div>
          <div ref="loadingIndicator" class="loading-indicator">
            Loading map...
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

export default {
  name: 'RouteMapModal',
  props: {
    route: {
      type: Object,
      required: true
    },
    vehicles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      map: null,
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      vehicleRoutes: [],
      routeLayers: [],
      locationMarkers: [],
      routeCache: {}
    };
  },
  watch: {
    route: {
      handler() {
        // Reinitialize when route data changes
        this.initializeVehicleRoutes();
        if (this.map) {
          // Clear existing data and redraw
          this.clearMapData();
          this.addLocationMarkers();
          this.displayRoutes();
          this.fitMapToRoute();
        }
      },
      deep: true
    }
  },
  mounted() {
    this.initializeVehicleRoutes();
    this.initializeMap();
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove();
    }
  },
  methods: {
    initializeVehicleRoutes() {
      if (!this.route.routes) return;
      
      const routeColors = this.generateRouteColors(this.route.routes.length);
      
      this.vehicleRoutes = this.route.routes.map((vehicleRoute, index) => {
        const vehicle = this.vehicles.find(v => v.id === vehicleRoute.vehicleId);
        return {
          index,
          name: this.getVehicleDisplayName(vehicleRoute, vehicle),
          color: routeColors[index],
          distance: vehicleRoute.distance || 0,
          visible: true,
          routeData: vehicleRoute
        };
      });
    },
    
    getVehicleDisplayName(vehicleRoute, vehicle) {
      if (this.getSolverType() === 'TSP' && this.route.routes.length === 1) {
        return 'Optimal Route';
      }
      
      if (vehicle) {
        return vehicle.plate;
      }
      
      return `Vehicle ${vehicleRoute.vehicle + 1}`;
    },
    
    getSolverType() {
      if (this.route.solver_type) {
        return this.route.solver_type.toUpperCase();
      }
      
      if ((this.route.demands && this.route.demands.some(d => d > 0)) || 
          this.route.vehicle_capacity || 
          this.route.vehicle_capacities) {
        return 'VRP';
      }
      
      if (this.route.routes && this.route.routes.length > 1) {
        return 'VRP';
      }
      
      return 'TSP';
    },
    
    generateRouteColors(numRoutes) {
      const colors = [
        '#4285F4', // Google Blue
        '#EA4335', // Google Red
        '#FBBC05', // Google Yellow
        '#34A853', // Google Green
        '#FF6D01', // Orange
        '#46BDC6', // Teal
        '#7C4DFF', // Purple
        '#1B9E77', // Green-blue
        '#D95F02', // Orange-brown
        '#7570B3', // Purple-blue
        '#E7298A', // Pink
        '#66A61E', // Green
        '#E6AB02', // Yellow-orange
        '#A6761D', // Brown
        '#666666', // Gray
      ];
      
      return Array(numRoutes).fill().map((_, i) => colors[i % colors.length]);
    },
    
    initializeMap() {
      mapboxgl.accessToken = this.accessToken;
      
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [28.9784, 41.0082], // Default center (Istanbul)
        zoom: 10
      });
      
      // Add controls
      this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
      this.map.addControl(new mapboxgl.ScaleControl(), 'bottom-left');
      
      this.map.on('load', () => {
        this.hideLoadingIndicator();
        this.setupMapSources();
        this.addLocationMarkers();
        this.displayRoutes();
        this.fitMapToRoute();
      });
    },
    
    setupMapSources() {
      // Add source for routes
      this.map.addSource('routes', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
    },
    
    /**
     * Create a mapping from location index to the routes that visit it
     * @returns {Object} locationIndex -> { routeIndices: [], colors: [], isDepot: boolean, hasInvisibleRoutes: boolean }
     */
    createLocationToRouteMapping() {
      const locationMapping = {};
      
      if (!this.route.routes || !this.vehicleRoutes) return locationMapping;
      
      // Initialize all locations
      if (this.route.allLocations) {
        this.route.allLocations.forEach((coords, index) => {
          locationMapping[index] = {
            routeIndices: [],
            colors: [],
            isDepot: this.isDepotLocation(index),
            hasInvisibleRoutes: false
          };
        });
      }
      
      // Map each route's visited locations (including invisible ones to track which locations should be hidden)
      this.route.routes.forEach((vehicleRoute, routeIndex) => {
        const vehicleInfo = this.vehicleRoutes[routeIndex];
        
        if (!vehicleInfo) return;
        
        // Track locations visited by this route (visible or not)
        const locationsToProcess = [];
        
        // Get locations from reach_times
        if (vehicleRoute.reach_times) {
          vehicleRoute.reach_times.forEach(reachTime => {
            locationsToProcess.push(reachTime.location_index);
          });
        }
        
        // Get locations from stops (fallback if no reach_times)
        if (vehicleRoute.stops && !vehicleRoute.reach_times) {
          vehicleRoute.stops.forEach(locationIndex => {
            locationsToProcess.push(locationIndex);
          });
        }
        
        // Process each location
        locationsToProcess.forEach(locationIndex => {
          if (locationMapping[locationIndex]) {
            if (vehicleInfo.visible) {
              // Add to visible routes
              locationMapping[locationIndex].routeIndices.push(routeIndex);
              locationMapping[locationIndex].colors.push(vehicleInfo.color);
            } else {
              // Mark that this location has invisible routes
              locationMapping[locationIndex].hasInvisibleRoutes = true;
            }
          }
        });
      });
      
      return locationMapping;
    },

    addLocationMarkers() {
      if (!this.route.allLocations || !this.route.locationNames) return;
      
      // Create location to route mapping
      const locationMapping = this.createLocationToRouteMapping();
      
      this.route.allLocations.forEach((coords, index) => {
        const isDepot = this.isDepotLocation(index);
        const locationName = this.route.locationNames[index] || `Location ${index}`;
        const demand = this.getLocationDemand(index);
        const arrivalTime = this.getLocationArrivalTime(index);
        const locationInfo = locationMapping[index] || { routeIndices: [], colors: [], isDepot, hasInvisibleRoutes: false };
        
        // Skip locations that are only visited by invisible vehicles (except depots)
        if (!isDepot && locationInfo.colors.length === 0 && locationInfo.hasInvisibleRoutes) {
          return;
        }
        
        // Determine marker color based on route assignment
        let markerColor = '#2ecc71'; // Default green for unassigned locations
        let markerClass = 'demand-marker';
        
        if (isDepot) {
          markerColor = '#e74c3c'; // Red for depot
          markerClass = 'depot-marker';
        } else if (locationInfo.colors.length > 0) {
          // Use the first assigned route's color
          markerColor = locationInfo.colors[0];
          markerClass = 'route-assigned-marker';
          
          // If multiple routes visit this location, add a special indicator
          if (locationInfo.colors.length > 1) {
            markerClass = 'multiple-routes-marker';
          }
        }
        
        // Create marker element
        const el = document.createElement('div');
        el.className = markerClass;
        
        // Set the marker color dynamically
        if (markerClass === 'route-assigned-marker') {
          el.style.backgroundColor = markerColor;
        } else if (markerClass === 'multiple-routes-marker') {
          // Create a gradient or mixed color for multiple routes
          if (locationInfo.colors.length === 2) {
            el.style.background = `linear-gradient(45deg, ${locationInfo.colors[0]} 50%, ${locationInfo.colors[1]} 50%)`;
          } else if (locationInfo.colors.length > 2) {
            // For more than 2 routes, use a multi-color gradient
            const gradientStops = locationInfo.colors.map((color, idx) => {
              const percentage = ((idx + 1) / locationInfo.colors.length) * 100;
              return `${color} ${percentage}%`;
            }).join(', ');
            el.style.background = `linear-gradient(45deg, ${gradientStops})`;
          }
        }
        
        // Create route assignment info for popup
        let routeAssignmentInfo = '';
        if (locationInfo.routeIndices.length > 0) {
          const routeNames = locationInfo.routeIndices.map(routeIndex => {
            const vehicle = this.vehicleRoutes[routeIndex];
            return `<span style="color: ${vehicle.color}; font-weight: bold;">${vehicle.name}</span>`;
          }).join(', ');
          routeAssignmentInfo = `<p><strong>Assigned to:</strong> ${routeNames}</p>`;
        }
        
        // Create popup content
        const popupContent = `
          <div class="location-popup">
            <h4>${locationName}</h4>
            <p><strong>Type:</strong> ${isDepot ? 'Depot' : 'Demand Point'}</p>
            ${routeAssignmentInfo}
            ${!isDepot && demand ? `<p><strong>Demand:</strong> ${demand} units</p>` : ''}
            ${arrivalTime ? `<p><strong>Arrival Time:</strong> ${arrivalTime}</p>` : ''}
            <p><strong>Coordinates:</strong> ${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}</p>
          </div>
        `;
        
        // Create marker with popup
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat(coords)
          .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(popupContent))
          .addTo(this.map);
        
        this.locationMarkers.push(marker);
      });
    },
    
    isDepotLocation(locationIndex) {
      if (this.route.multiDepot && this.route.depots) {
        return this.route.depots.includes(locationIndex);
      }
      return locationIndex === 0;
    },
    
    getLocationDemand(locationIndex) {
      if (this.isDepotLocation(locationIndex)) {
        return 0;
      }
      
      // Check reach_times first
      if (this.route.routes) {
        for (const vehicleRoute of this.route.routes) {
          if (vehicleRoute.reach_times) {
            const reachTime = vehicleRoute.reach_times.find(rt => rt.location_index === locationIndex);
            if (reachTime && reachTime.demand !== undefined) {
              return reachTime.demand;
            }
          }
        }
      }
      
      // Check demands array
      if (this.route.demands && this.route.demands[locationIndex] !== undefined) {
        return this.route.demands[locationIndex];
      }
      
      return 1; // Default
    },
    
    getLocationArrivalTime(locationIndex) {
      if (!this.route.routes) return null;
      
      for (const vehicleRoute of this.route.routes) {
        if (vehicleRoute.reach_times) {
          const reachTime = vehicleRoute.reach_times.find(rt => rt.location_index === locationIndex);
          if (reachTime) {
            if (reachTime.actual_arrival_time) {
              return reachTime.actual_arrival_time;
            }
            if (reachTime.reach_time_minutes !== undefined) {
              return this.formatRealTime(reachTime.reach_time_minutes);
            }
          }
        }
      }
      
      return null;
    },
    
    formatRealTime(minutes) {
      if (!minutes && minutes !== 0) return 'N/A';
      
      // Default start time is 8:00 AM
      const startHour = 8;
      const startMinute = 0;
      
      const totalStartMinutes = startHour * 60 + startMinute;
      const totalMinutes = totalStartMinutes + minutes;
      
      const hours = Math.floor(totalMinutes / 60) % 24;
      const mins = Math.floor(totalMinutes % 60);
      
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    },
    
    async displayRoutes() {
      if (!this.route.routes || !this.route.allLocations) return;
      
      this.showLoadingIndicator();
      
      const allFeatures = [];
      
      // Process each visible vehicle route
      for (let routeIndex = 0; routeIndex < this.route.routes.length; routeIndex++) {
        const vehicleRoute = this.route.routes[routeIndex];
        const vehicle = this.vehicleRoutes[routeIndex];
        
        if (!vehicle || !vehicle.visible) continue;
        
        const routeStops = vehicleRoute.stops;
        if (!routeStops || routeStops.length < 2) continue;
        
        console.log(`Processing route ${routeIndex} with ${routeStops.length} stops`);
        
        // Create features for each segment
        for (let i = 0; i < routeStops.length - 1; i++) {
          const startPoint = this.route.allLocations[routeStops[i]];
          const endPoint = this.route.allLocations[routeStops[i + 1]];
          
          if (!startPoint || !endPoint) {
            console.warn(`Missing location data for stops ${routeStops[i]} or ${routeStops[i + 1]}`);
            continue;
          }
          
          console.log(`Getting geometry from ${startPoint} to ${endPoint}`);
          
          try {
            // Get route geometry from Mapbox
            const routeGeometry = await this.getRouteGeometry(startPoint, endPoint);
            
            if (routeGeometry && routeGeometry.length > 1) {
              const feature = {
                type: 'Feature',
                properties: {
                  routeId: routeIndex,
                  color: vehicle.color,
                  vehicleName: vehicle.name,
                  segmentIndex: i
                },
                geometry: {
                  type: 'LineString',
                  coordinates: routeGeometry
                }
              };
              
              allFeatures.push(feature);
              console.log(`Added feature for route ${routeIndex}, segment ${i}`);
            }
          } catch (error) {
            console.error(`Error processing segment ${i} for route ${routeIndex}:`, error);
          }
        }
      }
      
      console.log(`Total features created: ${allFeatures.length}`);
      
      // Update route source
      if (this.map.getSource('routes')) {
        this.map.getSource('routes').setData({
          type: 'FeatureCollection',
          features: allFeatures
        });
      }
      
      // Add route layers
      this.addRouteLayers();
      
      this.hideLoadingIndicator();
    },
    
    async getRouteGeometry(start, end) {
      // Create cache key for this route segment
      const cacheKey = `${start.join(',')}-${end.join(',')}`;
      
      // Check cache first
      if (this.routeCache && this.routeCache[cacheKey]) {
        return this.routeCache[cacheKey];
      }
      
      try {
        // Use Mapbox Directions API
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson&access_token=${this.accessToken}`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.routes && data.routes.length > 0) {
          const geometry = data.routes[0].geometry.coordinates;
          
          // Cache the result
          if (!this.routeCache) {
            this.routeCache = {};
          }
          this.routeCache[cacheKey] = geometry;
          
          return geometry;
        }
      } catch (error) {
        console.warn('Error fetching route geometry from Mapbox:', error);
        
        // Try OSRM as fallback
        try {
          const osrmUrl = `http://router.project-osrm.org/route/v1/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson`;
          const osrmResponse = await fetch(osrmUrl);
          const osrmData = await osrmResponse.json();
          
          if (osrmData.routes && osrmData.routes.length > 0) {
            const geometry = osrmData.routes[0].geometry.coordinates;
            
            // Cache the result
            if (!this.routeCache) {
              this.routeCache = {};
            }
            this.routeCache[cacheKey] = geometry;
            
            return geometry;
          }
        } catch (osrmError) {
          console.warn('Error fetching route geometry from OSRM:', osrmError);
        }
      }
      
      // Fallback to straight line
      console.warn('Using straight line fallback for route segment');
      return [start, end];
    },
    
    addRouteLayers() {
      // Remove existing route layers
      this.routeLayers.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      });
      this.routeLayers = [];
      
      // Add layers for each visible vehicle
      this.vehicleRoutes.forEach((vehicle, index) => {
        if (!vehicle.visible) return;
        
        const layerId = `route-${index}`;
        
        this.map.addLayer({
          id: layerId,
          type: 'line',
          source: 'routes',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': vehicle.color,
            'line-width': 4,
            'line-opacity': 0.8
          },
          filter: ['==', ['get', 'routeId'], index]
        });
        
        this.routeLayers.push(layerId);
      });
    },
    
    toggleVehicleVisibility(index) {
      this.vehicleRoutes[index].visible = !this.vehicleRoutes[index].visible;
      this.displayRoutes();
      this.refreshLocationMarkers();
    },
    
    /**
     * Refresh location markers when route visibility changes
     */
    refreshLocationMarkers() {
      // Remove existing markers
      this.locationMarkers.forEach(marker => {
        marker.remove();
      });
      this.locationMarkers = [];
      
      // Add markers with updated visibility
      this.addLocationMarkers();
    },
    
    fitMapToRoute() {
      if (!this.route.allLocations || this.route.allLocations.length === 0) return;
      
      const bounds = new mapboxgl.LngLatBounds();
      
      this.route.allLocations.forEach(location => {
        bounds.extend(location);
      });
      
      this.map.fitBounds(bounds, {
        padding: 50
      });
    },
    
    showLoadingIndicator() {
      if (this.$refs.loadingIndicator) {
        this.$refs.loadingIndicator.classList.add('visible');
      }
    },
    
    hideLoadingIndicator() {
      if (this.$refs.loadingIndicator) {
        this.$refs.loadingIndicator.classList.remove('visible');
      }
    },
    
    /**
     * Clear existing map data (markers and routes)
     */
    clearMapData() {
      // Remove existing markers
      this.locationMarkers.forEach(marker => {
        marker.remove();
      });
      this.locationMarkers = [];
      
      // Remove existing route layers
      this.routeLayers.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      });
      this.routeLayers = [];
      
      // Clear route data source
      if (this.map.getSource('routes')) {
        this.map.getSource('routes').setData({
          type: 'FeatureCollection',
          features: []
        });
      }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 95vw;
  height: 90vh;
  max-width: 1200px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.4rem;
  font-weight: 600;
}

.btn-close {
  background: none;
  border: none;
  font-size: 2rem;
  color: #7f8c8d;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-close:hover {
  background-color: #f8f9fa;
  color: #2c3e50;
}

.modal-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.map-controls {
  padding: 1rem 2rem;
  border-bottom: 1px solid #eee;
  background-color: #f8f9fa;
  flex-shrink: 0;
}

.vehicle-controls h4 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.vehicle-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.vehicle-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background: white;
  border: 1px solid #dee2e6;
  transition: all 0.2s;
}

.vehicle-checkbox:hover {
  border-color: #3498db;
  box-shadow: 0 2px 4px rgba(52, 152, 219, 0.1);
}

.vehicle-checkbox input[type="checkbox"] {
  margin: 0;
}

.checkbox-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.vehicle-name {
  font-weight: 600;
  color: #2c3e50;
  min-width: 100px;
}

.vehicle-stats {
  font-size: 0.9rem;
  color: #7f8c8d;
  font-weight: 500;
}

.map-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.map-display {
  width: 100%;
  height: 100%;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  z-index: 10;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s;
}

.loading-indicator.visible {
  opacity: 1;
}

/* Global marker styles */
:global(.depot-marker) {
  background-color: #e74c3c;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  cursor: pointer;
}

:global(.demand-marker) {
  background-color: #2ecc71;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

:global(.route-assigned-marker) {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  transition: all 0.2s ease;
}

:global(.route-assigned-marker:hover) {
  transform: scale(1.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

:global(.multiple-routes-marker) {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

:global(.multiple-routes-marker:hover) {
  transform: scale(1.2);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.6);
}

:global(.multiple-routes-marker::after) {
  content: '';
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #fff;
  border: 1px solid #333;
  border-radius: 50%;
  font-size: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:global(.location-popup) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  max-width: 300px;
}

:global(.location-popup h4) {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #ecf0f1;
  padding-bottom: 8px;
}

:global(.location-popup p) {
  margin: 5px 0;
  font-size: 14px;
  line-height: 1.4;
  color: #34495e;
}

:global(.location-popup strong) {
  color: #2c3e50;
  font-weight: 600;
}

/* Responsive design */
@media (max-width: 768px) {
  .modal-content {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    max-width: none;
    max-height: none;
  }
  
  .modal-header {
    padding: 1rem;
  }
  
  .modal-header h2 {
    font-size: 1.2rem;
  }
  
  .map-controls {
    padding: 1rem;
  }
  
  .vehicle-checkboxes {
    gap: 0.5rem;
  }
  
  .vehicle-checkbox {
    padding: 0.4rem 0.6rem;
    font-size: 0.9rem;
  }
  
  .vehicle-name {
    min-width: 80px;
  }
}

@media (max-width: 480px) {
  .vehicle-checkboxes {
    flex-direction: column;
  }
  
  .vehicle-checkbox {
    justify-content: space-between;
  }
}
</style>